import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import accountService from '../../services/authService';
import { useRef } from 'react';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import {
  Box,
  Typography,
  Button,
  ListItemAvatar,
  Card,
  CardContent,
  Grid,
  Stack,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
  Tooltip,
  CardActions,
  Badge
} from '@mui/material';
import {
  YouTube as YouTubeIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
  BarChart as BarChartIcon,
  Facebook as FacebookIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Launch as LaunchIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  VideoLibrary as VideoLibraryIcon,
  Article as ArticleIcon,
  Business as BusinessIcon,
  Photo as PhotoIcon,
  Movie as MovieIcon,
  Campaign as CampaignIcon,
  ThumbUp as ThumbUpIcon,
  Comment as CommentIcon
} from '@mui/icons-material';

// Instagram gradient
const instagramGradient = 'linear-gradient(45deg, #feda75 0%, #fa7e1e 25%, #d62976 50%, #962fbf 75%, #4f5bd5 100%)';

// TikTok Icon Component
const TikTokIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

// X/Twitter Icon Component
const XIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const platforms = [
  {
    id: 'youtube',
    name: 'YouTube',
    icon: YouTubeIcon,
    color: '#FF0000',
    bgColor: '#FFF5F5',
    gradient: 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)',
    description: 'Share videos and reach millions of viewers',
    features: ['Video uploads', 'Live streaming', 'Community posts', 'Shorts'],
    authFields: [
      { name: 'email', label: 'YouTube Email', type: 'email', required: true },
      { name: 'channelId', label: 'Channel ID', type: 'text', required: false }
    ]
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    icon: LinkedInIcon,
    color: '#0077B5',
    bgColor: '#F0F8FF',
    gradient: 'linear-gradient(135deg, #0077B5 0%, #005582 100%)',
    description: 'Connect with professionals and share industry insights',
    features: ['Professional posts', 'Articles', 'Company updates', 'Networking'],
    authFields: [
      { name: 'email', label: 'LinkedIn Email', type: 'email', required: true },
      { name: 'companyPage', label: 'Company Page', type: 'text', required: false }
    ]
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: InstagramIcon,
    color: '#E4405F',
    bgColor: '#FEF7F7',
    gradient: instagramGradient,
    description: 'Share photos, stories, and connect with your audience',
    features: ['Photo posts', 'Stories', 'Reels', 'IGTV'],
    authFields: [
      { name: 'username', label: 'Instagram Username', type: 'text', required: true },
      { name: 'businessAccount', label: 'Business Account', type: 'select', options: ['Personal', 'Business', 'Creator'], required: true }
    ]
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: FacebookIcon,
    color: '#1877F2',
    bgColor: '#F0F6FF',
    gradient: 'linear-gradient(135deg, #1877F2 0%, #0866ff 100%)',
    description: 'Connect with friends and share updates',
    features: ['Posts', 'Stories', 'Events', 'Pages'],
    authFields: [
      { name: 'email', label: 'Facebook Email', type: 'email', required: true },
      { name: 'pageId', label: 'Page ID (Optional)', type: 'text', required: false }
    ]
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    icon: TikTokIcon,
    color: '#000000',
    bgColor: '#F8F8F8',
    gradient: 'linear-gradient(135deg, #000000 0%, #333333 100%)',
    description: 'Create short-form videos and go viral',
    features: ['Short videos', 'Effects', 'Sounds', 'Trends'],
    authFields: [
      { name: 'username', label: 'TikTok Username', type: 'text', required: true },
      { name: 'creatorFund', label: 'Creator Fund Eligible', type: 'select', options: ['Yes', 'No'], required: false }
    ]
  },
  {
    id: 'twitter',
    name: 'X (Twitter)',
    icon: XIcon,
    color: '#000000',
    bgColor: '#F8F8F8',
    gradient: 'linear-gradient(135deg, #080808ff 0%, #070707ff 100%)',
    description: 'Share updates and join the global conversation',
    features: ['Tweets', 'Analytics', 'Spaces', 'Community Engagement'],
    authFields: [] // OAuth flow doesn't require a manual input dialog
  }
];

const TwitterStatsCard = ({ stats, accountName, onViewStats }) => (
  <Card
    sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      borderRadius: 4,
      boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 16px 48px rgba(0,0,0,0.12)'
      }
    }}
  >
    <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Avatar
          sx={{
            bgcolor: '#050505ff',
            width: { xs: 48, md: 56 },
            height: { xs: 48, md: 56 },
            boxShadow: '0 4px 16px rgba(29,161,242,0.3)'
          }}
        >
          <XIcon htmlColor="white" sx={{ fontSize: { xs: 24, md: 28 } }} />
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            X (Twitter)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {accountName}
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Tweets
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {Number(stats?.tweetCount || 0).toLocaleString()}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Followers
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {Number(stats?.followersCount || 0).toLocaleString()}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Likes
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {Number(stats?.likesCount || stats?.retweetCount || 0).toLocaleString()}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Following
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {Number(stats?.followingCount || 0).toLocaleString()}
          </Typography>
        </Grid>
      </Grid>
    </CardContent>

    <Divider />

    <CardActions sx={{ p: 2 }}>
      <Button
        fullWidth
        variant="outlined"
        color="primary"
        startIcon={<BarChartIcon />}
        onClick={onViewStats}
        sx={{
          borderRadius: 2,
          py: 1,
          textTransform: 'none',
          fontWeight: 600
        }}
      >
        View Analytics
      </Button>
    </CardActions>
  </Card>
);

// Also, here's the matching LinkedInStatsCard for consistency:
const LinkedInStatsCard = ({ stats, accountName, onViewStats }) => (
  <Card
    sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      borderRadius: 4,
      boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 16px 48px rgba(0,0,0,0.12)'
      }
    }}
  >
    <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Avatar
          sx={{
            bgcolor: '#0077B5',
            width: { xs: 48, md: 56 },
            height: { xs: 48, md: 56 },
            boxShadow: '0 4px 16px rgba(0,119,181,0.3)'
          }}
        >
          <LinkedInIcon sx={{ color: 'white', fontSize: { xs: 24, md: 28 } }} />
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            LinkedIn
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {accountName}
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Followers
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {Number(stats?.followerCount || 0).toLocaleString()}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Impressions
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {Number(stats?.impressionCount || 0).toLocaleString()}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Engagements
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {Number(stats?.engagementCount || 0).toLocaleString()}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Posts
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {Number(stats?.postCount || 0).toLocaleString()}
          </Typography>
        </Grid>
      </Grid>
    </CardContent>

    <Divider />

    <CardActions sx={{ p: 2 }}>
      <Button
        fullWidth
        variant="outlined"
	title = "Here User can manage their posts , videos , images . They can delete , update the contents from here "
        color="primary"
        startIcon={<BarChartIcon />}
        onClick={onViewStats}
        sx={{
          borderRadius: 2,
          py: 1,
          textTransform: 'none',
          fontWeight: 600
        }}
      >
        View Recent Stats
      </Button>
    </CardActions>
  </Card>
);

// And the matching YouTubeStatsCard:
const YouTubeStatsCard = ({ stats, accountName, onViewStats }) => (
  <Card
    sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      borderRadius: 4,
      boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 16px 48px rgba(0,0,0,0.12)'
      }
    }}
  >
    <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Avatar
          sx={{
            bgcolor: '#FF0000',
            width: { xs: 48, md: 56 },
            height: { xs: 48, md: 56 },
            boxShadow: '0 4px 16px rgba(255,0,0,0.3)'
          }}
        >
          <YouTubeIcon sx={{ fontSize: { xs: 24, md: 28 }, color: 'white' }} />
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            YouTube
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {accountName}
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={4}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Subscribers
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {Number(stats?.subscriberCount || 0).toLocaleString()}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Total Views
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {Number(stats?.viewCount || 0).toLocaleString()}
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Videos
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {Number(stats?.videoCount || 0).toLocaleString()}
          </Typography>
        </Grid>
      </Grid>
    </CardContent>

    <Divider />

    <CardActions sx={{ p: 2 }}>
      <Button
        fullWidth
        variant="outlined"
        color="secondary"
        title = "Here User can manage their posts , videos , images . They can delete , update the contents from here "
        startIcon={<BarChartIcon />}
        onClick={onViewStats}
        sx={{
          borderRadius: 2,
          py: 1,
          textTransform: 'none',
          fontWeight: 600
        }}
      >
        View Recent Stats
      </Button>
    </CardActions>
  </Card>
);


const FacebookStatsCard = ({ accountName, totalFriends, totalFollowers, totalPosts, onViewStats }) => (
  <Card
    sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      borderRadius: 4,
      boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 16px 48px rgba(0,0,0,0.12)',
      },
    }}
  >
    <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Avatar
          sx={{
            bgcolor: '#1877F2',
            width: { xs: 48, md: 56 },
            height: { xs: 48, md: 56 },
            boxShadow: '0 4px 16px rgba(24,119,242,0.3)',
          }}
        >
          <FacebookIcon sx={{ fontSize: { xs: 24, md: 28 }, color: 'white' }} />
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Facebook
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {accountName || '–'}
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Total Friends
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {totalFriends != null ? totalFriends : '–'}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Total Followers
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {totalFollowers != null ? totalFollowers : '–'}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            Total Posts
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {totalPosts != null ? totalPosts : '–'}
          </Typography>
        </Grid>
      </Grid>

      <Divider />
    </CardContent>

    <CardActions sx={{ p: 2 }}>
      <Button
        fullWidth
        variant="outlined"
        color="primary"
        startIcon={<BarChartIcon />}
title = "Here user can manage their posts , videos , images . They can delete , update the contents from here "
        onClick={onViewStats}
        sx={{
          borderRadius: 2,
          py: 1,
          textTransform: 'none',
          fontWeight: 600,
        }}
      >
        View Recent Stats
      </Button>
    </CardActions>
  </Card>
);


export default function SocialAccounts() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const scrollContainerRef = useRef(null);

  const isResettingRef = useRef(false);
  const lastScrollTimeRef = useRef(0);
  const autoScrollTimeout = useRef(null);

  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [authData, setAuthData] = useState({});
  const [isConnecting, setIsConnecting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [connectionStep, setConnectionStep] = useState(1);
  const [verificationCode, setVerificationCode] = useState('');
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [youtubeStats, setYoutubeStats] = useState(null);
  const [selectedAccountForVideos, setSelectedAccountForVideos] = useState(null);
  const [videosDialogOpen, setVideosDialogOpen] = useState(false);
  const [recentVideos, setRecentVideos] = useState([]);
  const [searchParams] = useSearchParams();
  const [isHovered, setIsHovered] = useState(false);

  const handleOpenVideosDialog = (account) => {
    setSelectedAccountForVideos(account);
    setRecentVideos(account.lastVideos || []);
    setVideosDialogOpen(true);
  };

  const handleCloseVideosDialog = () => {
    setVideosDialogOpen(false);
    setSelectedAccountForVideos(null);
    setRecentVideos([]);
  };

  // Manual smooth scroll function for consistent animations
  const smoothScrollTo = (element, target, duration = 600) => {
    if (!element) return;
    const start = element.scrollLeft;
    const change = target - start;
    const startTime = performance.now();

    function animateScroll(time) {
      const elapsed = time - startTime;
      const progress = Math.min(elapsed / duration, 1);
      element.scrollLeft = start + change * progress;

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    }

    requestAnimationFrame(animateScroll);
  };

  // Auto scroll with manual animation
  const autoScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const target = container.scrollLeft + container.clientWidth;
    smoothScrollTo(container, target, 600);
  };

  // Auto swipe interval with hover pause and cooldown
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (!isHovered) {
      const intervalId = setInterval(() => {
        if (!isResettingRef.current && performance.now() - lastScrollTimeRef.current > 650) {
          autoScroll();
          lastScrollTimeRef.current = performance.now();
        }
      }, 3000);

      return () => clearInterval(intervalId);
    }
  }, [isHovered]);

  // API fetching with token handling

  useEffect(() => {
  const fetchConnectedAccounts = async (jwt) => {
    setIsLoading(true);
    const API_BASE_URL =
      import.meta.env.VITE_API_URL || "https://socialmedia.gtlit.com/api";

    // Get userId from the URL (null if not present)
    const params = new URLSearchParams(window.location.search);
    const userId = params.get("userId");

    try {
      const response = await fetch(
        `${API_BASE_URL}/accounts/connected`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${jwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: userId
          }),
        }
      );
      if (!response.ok) throw new Error(`Status ${response.status}`);
      const data = await response.json();
      const normalized = (Array.isArray(data) ? data : []).map(acc => ({
        id: `${acc.platformType?.toLowerCase?.() || "unknown"}-${acc.accountId || "na"}`,
        platform: acc.platformType?.toLowerCase?.() || "unknown",
        accountName: acc.accountName || "",
        accountId: acc.accountId || "",
        stats: acc.stats || {},
        connected: acc.connected ?? acc.isConnected ?? false,
        lastVideos: acc.lastVideos || [],
      }));
      setConnectedAccounts(normalized);
    } catch (error) {
      console.error("API call FAILED:", error);
      setConnectedAccounts([]); // Empty on error
    } finally {
      setIsLoading(false);
    }
  };

  // Handle token from URL
  const params = new URLSearchParams(window.location.search);
  const tokenFromUrl = params.get("token");
  if (tokenFromUrl) {
    localStorage.setItem("token", tokenFromUrl);
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  const jwt = localStorage.getItem("token");
  if (jwt) {
    fetchConnectedAccounts(jwt);
  } else {
    setIsLoading(false);
  }
}, []);

  // Helper Functions
  const setCookie = (name, value, minutes) => {
    let expires = '';
    if (minutes) {
      const date = new Date();
      date.setTime(date.getTime() + minutes * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = `${name}=${value || ''}${expires}; path=/; SameSite=Lax; Secure`;
  };

  const getCookie = (name) => {
    const cookieString = `; ${document.cookie}`;
    const parts = cookieString.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  function parseJwt(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return null;
    }
  }
  const handleTwitterConnect = () => {
    const token = localStorage.getItem('token');
    const twitterToken = localStorage.getItem('twitter_token');

    if (!token || !twitterToken) {
      alert('Login session expired. Please log in again.');
      return;
    }

    const decoded = parseJwt(twitterToken);
    const userId = decoded.user_id || decoded.userId || decoded.sub; // try common JWT claim keys
    if (!userId) {
      alert('User ID not found in twitter token.');
      return;
    }

    if (!document.cookie.includes('portal_jwt')) {
      const expires = new Date(Date.now() + 5 * 60000).toUTCString(); // 5 min expiry
      document.cookie = `portal_jwt=${encodeURIComponent(token)}; expires=${expires}; path=/; Secure; SameSite=Strict`;
    }

    window.location.href = `https://socialmedia.gtlit.com/api/twitter/initiate?userId=${encodeURIComponent(userId)}`;
  };

  // --- Main Click Handler ---
  // --- Add LinkedIn connect handler ---
  const handleLinkedInConnect = () => {
    const token = localStorage.getItem('token');
    const twitterToken = localStorage.getItem('twitter_token');
    if (!token) {
      alert('Login session expired. Please log in again.');
      return;
    }

    // Ensure backend has user context via portal_jwt cookie
    const portalJwt = getCookie('portal_jwt');
    if (!portalJwt) {
      setCookie('portal_jwt', token, 5); // Expires in 5 minutes
    }

    // Context to pass to backend OAuth2, e.g. to identify user and redirection after connect
    const customState = {
      source: 'connecting_account',
      platform: 'linkedin',
      redirect_uri: '/account'
    };
    const encodedState = encodeURIComponent(JSON.stringify(customState));
    setCookie('oauth2_redirect_context', encodedState, 5);
    const decoded = parseJwt(twitterToken);
    const userId = decoded.user_id || decoded.userId || decoded.sub; // try common JWT claim keys
    if (!userId) {
      alert('User ID not found in twitter token.');
      return;
    }

    if (!document.cookie.includes('portal_jwt')) {
      const expires = new Date(Date.now() + 5 * 60000).toUTCString(); // 5 min expiry
      document.cookie = `portal_jwt=${encodeURIComponent(token)}; expires=${expires}; path=/; Secure; SameSite=Strict`;
    }

    // Redirect to backend API that initiates LinkedIn OAuth2 with PKCE flow
    window.location.href = `https://socialmedia.gtlit.com/api/linkedin/initiate?userId=${encodeURIComponent(userId)}`;
  };

  const handleConnectClick = (platform) => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams(window.location.search);
    const userId = params.get('userId'); // Returns the value if present, or null if not
    
    setCookie('user_id', userId, 5);

    if (!token) {
      alert('Login session not found. Please log in again.');
      return;
    }

    const portalJwtCookie = getCookie('portal_jwt');

    if (!portalJwtCookie) {
      console.log('portal_jwt cookie not found. Creating it from localStorage token...');
      setCookie('portal_jwt', token, 5);
    }

    const customState = {
      source: 'connecting_account',
      platform: platform.id,
      redirect_uri: '/account'
    };

    setCookie('oauth2_context', 'platform_connect', 5);
    const encodedState = encodeURIComponent(JSON.stringify(customState));
    const baseUrl = 'https://socialmedia.gtlit.com/api/oauth2/authorization/';

    if (platform.id === 'youtube') {
      // highlight-start
      window.location.href = `${baseUrl}google?token=${token}`;
      // highlight-end
    } else if (platform.id === 'twitter') {
      const twitterToken = localStorage.getItem('twitter_token');

      if (!token || !twitterToken) {
        alert('Login session expired. Please log in again.');
        return;
      }

      const decoded = parseJwt(twitterToken);
      const userId = decoded.user_id || decoded.userId || decoded.sub; // try common JWT claim keys
      if (!userId) {
        alert('User ID not found in twitter token.');
        return;
      }

      if (!document.cookie.includes('portal_jwt')) {
        const expires = new Date(Date.now() + 5 * 60000).toUTCString(); // 5 min expiry
        document.cookie = `portal_jwt=${encodeURIComponent(token)}; expires=${expires}; path=/; Secure; SameSite=Strict`;
      }

      window.location.href = `https://socialmedia.gtlit.com/api/twitter/initiate?userId=${encodeURIComponent(userId)}`;
    } else if (platform.id === 'facebook') {
		
	const twitterToken = localStorage.getItem('twitter_token');
      if (!token) {
        alert('Login session expired. Please log in again.');
        return;
      }

      if (!portalJwtCookie) {
        setCookie('portal_jwt', token, 5); // Expires in 5 minutes
      }

      const encodedState = encodeURIComponent(JSON.stringify(customState));
      setCookie('oauth2_redirect_context', encodedState, 5);
      const decoded = parseJwt(twitterToken);
      const userId = decoded.user_id || decoded.userId || decoded.sub; // try common JWT claim keys
      if (!userId) {
        alert('User ID not found in twitter token.');
        return;
      }

      if (!document.cookie.includes('portal_jwt')) {
        const expires = new Date(Date.now() + 5 * 60000).toUTCString(); // 5 min expiry
        document.cookie = `portal_jwt=${encodeURIComponent(token)}; expires=${expires}; path=/; Secure; SameSite=Strict`;
      }

      // Redirect to backend API that initiates LinkedIn OAuth2 with PKCE flow
      window.location.href = `https://socialmedia.gtlit.com/api/facebook/initiate?userId=${encodeURIComponent(userId)}`;



     } else if (platform.id === 'linkedin') {
      // highlight-start
      const twitterToken = localStorage.getItem('twitter_token');
      if (!token) {
        alert('Login session expired. Please log in again.');
        return;
      }

      if (!portalJwtCookie) {
        setCookie('portal_jwt', token, 5); // Expires in 5 minutes
      }

      const encodedState = encodeURIComponent(JSON.stringify(customState));
      setCookie('oauth2_redirect_context', encodedState, 5);
      const decoded = parseJwt(twitterToken);
      const userId = decoded.user_id || decoded.userId || decoded.sub; // try common JWT claim keys
      if (!userId) {
        alert('User ID not found in twitter token.');
        return;
      }

      if (!document.cookie.includes('portal_jwt')) {
        const expires = new Date(Date.now() + 5 * 60000).toUTCString(); // 5 min expiry
        document.cookie = `portal_jwt=${encodeURIComponent(token)}; expires=${expires}; path=/; Secure; SameSite=Strict`;
      }

      // Redirect to backend API that initiates LinkedIn OAuth2 with PKCE flow
      window.location.href = `https://socialmedia.gtlit.com/api/linkedin/initiate?userId=${encodeURIComponent(userId)}`;
      // highlight-end
    } else {
      setSelectedPlatform(platform);
    }
  };
  const handleAuthFieldChange = (fieldName, value) => {
    setAuthData((prev) => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleDisconnectAccount = async (platformId) => {
    const params = new URLSearchParams(window.location.search);
    const userId = params.get("userId");
    const token = localStorage.getItem('twitter_token');
    
    try {
      await accountService.removeAccount(platformId , token , userId);
      setConnectedAccounts((prev) =>
        prev.filter((acc) => {
          const p = acc.platform ?? acc.platformType?.toLowerCase();
          return p !== platformId.toLowerCase();
        })
      );
      alert(`${platformId} account removed successfully`);
    } catch (error) {
      console.error('Failed to disconnect:', error);
      alert('Could not remove account. Try again.');
    }
  };

  const handleFacebookConnect = () => {
    // 1. Get the token from localStorage (to prove the user is logged in).
    const token = localStorage.getItem('token');

    // 2. Make sure the user has an active session.
   const twitterToken = localStorage.getItem('twitter_token');
      if (!token) {
        alert('Login session expired. Please log in again.');
        return;
      }

      if (!portalJwtCookie) {
        setCookie('portal_jwt', token, 5); // Expires in 5 minutes
      }

      const encodedState = encodeURIComponent(JSON.stringify(customState));
      setCookie('oauth2_redirect_context', encodedState, 5);
      const decoded = parseJwt(twitterToken);
      const userId = decoded.user_id || decoded.userId || decoded.sub; // try common JWT claim keys
      if (!userId) {
        alert('User ID not found in twitter token.');
        return;
      }

      if (!document.cookie.includes('portal_jwt')) {
        const expires = new Date(Date.now() + 5 * 60000).toUTCString(); // 5 min expiry
        document.cookie = `portal_jwt=${encodeURIComponent(token)}; expires=${expires}; path=/; Secure; SameSite=Strict`;
      }

      // Redirect to backend API that initiates LinkedIn OAuth2 with PKCE flow
      window.location.href = `https://socialmedia.gtlit.com/api/facebook/initiate?userId=${encodeURIComponent(userId)}`;


  };

  const handleVerification = async () => {
    setIsConnecting(true);

    setTimeout(() => {
      const newAccount = {
        platformType: selectedPlatform.id.toUpperCase(),
        accountName: authData.username || authData.email?.split('@')[0] || 'user',
        accountId: `${selectedPlatform.id}-${Date.now()}`,
        connected: true,
        stats: {}
      };

      setConnectedAccounts((prev) => [...prev, newAccount]);
      setConnectionStep(3);
      setIsConnecting(false);
    }, 1500);
  };

  const handleDisconnect = (accountId) => {
    setConnectedAccounts((prev) => prev.filter((acc) => acc.accountId !== accountId));
  };

  const handleCloseDialog = () => {
    setSelectedPlatform(null);
    setAuthData({});
    setConnectionStep(1);
    setVerificationCode('');
  };

  const getPlatformData = (platformId) => {
    return platforms.find((p) => p.id === platformId?.toLowerCase());
  };

  const getFeatureIcon = (feature) => {
    const iconMap = {
      'Video uploads': VideoLibraryIcon,
      'Live streaming': MovieIcon,
      'Community posts': PeopleIcon,
      Shorts: VideoLibraryIcon,
      'Professional posts': BusinessIcon,
      Articles: ArticleIcon,
      'Company updates': CampaignIcon,
      Networking: PeopleIcon,
      'Photo posts': PhotoIcon,
      Stories: MovieIcon,
      Reels: VideoLibraryIcon,
      IGTV: VideoLibraryIcon,
      Posts: ArticleIcon,
      Events: ScheduleIcon,
      Pages: BusinessIcon,
      'Short videos': VideoLibraryIcon,
      Effects: MovieIcon,
      Sounds: MovieIcon,
      Trends: TrendingUpIcon,
      Tweets: ArticleIcon,
      Analytics: BarChartIcon,
      Spaces: PeopleIcon,
      'Community Engagement': PeopleIcon
    };

    const IconComponent = iconMap[feature] || InfoIcon;
    return <IconComponent sx={{ fontSize: 16 }} />;
  };

  // Infinite scroll with reset flag and cooldown
  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const sectionWidth = scrollWidth / 3;

    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);

    if (scrollLeft <= 5) {
      isResettingRef.current = true;
      container.scrollTo({ left: sectionWidth + 5, behavior: 'auto' });
      lastScrollTimeRef.current = performance.now();

      clearTimeout(autoScrollTimeout.current);
      autoScrollTimeout.current = setTimeout(() => {
        isResettingRef.current = false;
      }, 650);
    } else if (scrollLeft >= sectionWidth * 2 - 5) {
      isResettingRef.current = true;
      container.scrollTo({ left: sectionWidth - 5, behavior: 'auto' });
      lastScrollTimeRef.current = performance.now();

      clearTimeout(autoScrollTimeout.current);
      autoScrollTimeout.current = setTimeout(() => {
        isResettingRef.current = false;
      }, 650);
    }
  };

  // Navigation functions with smooth scrolling
  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const target = container.scrollLeft - 360;
      smoothScrollTo(container, target, 400);
    }
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const target = container.scrollLeft + 360;
      smoothScrollTo(container, target, 400);
    }
  };

  // Initialize scroll position
  useEffect(() => {
    if (platforms.length === 0) return;

    const initializeScroll = () => {
      const container = scrollContainerRef.current;
      if (container) {
        setTimeout(() => {
          const sectionWidth = container.scrollWidth / 3;
          container.scrollTo({ left: sectionWidth, behavior: 'auto' });

          setTimeout(() => {
            setCanScrollLeft(true);
            setCanScrollRight(true);
          }, 100);
        }, 100);
      }
    };

    initializeScroll();
  }, [platforms]);

      return (
    <Box sx={{
      width: '100%',
      minHeight: '100vh',
      p: { xs: 2, md: 4 },
      borderRadius: 4,
      background: '#f4f1ff',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
    }}>
      {/* Header */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography
          variant={isMobile ? "h3" : "h1"}
          sx={{
            fontWeight: '800',
            background: 'linear-gradient(90deg,rgba(2, 0, 36, 1) 0%, rgba(9, 9, 121, 1) 35%, rgba(0, 212, 255, 1) 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1
          }}
        >
          Social Accounts
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: 'black',

            mb: 3,
            fontWeight: 700,
            fontSize: { xs: '1rem', md: '1.35rem' }
          }}
        >
          Connect and manage your social media accounts
        </Typography>
        {isLoading ? (
          <CircularProgress color="primary" />
        ) : (
          <Chip
            label={`${connectedAccounts.length} accounts connected`}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              fontWeight: 600,
              px: 2,
              py: 1,
              fontSize: { xs: '0.8rem', md: '0.9rem' }
            }}
          />
        )}

        {/* Connected Accounts Section */}
        {connectedAccounts.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography variant="h5" sx={{ mb: 4, fontWeight: 700, color: '#1e293b' }}>
              Connected Accounts
            </Typography>
            <Grid container spacing={3}>
              {connectedAccounts.map(account => {
                // YouTube account handling
                if (account.platform === 'youtube') {
                  if (account.connected && account.stats) {
                    // ✅ Normal YouTube stats card
                    return (
                      <Grid item xs={12} md={6} key={account.accountId}>
                        <YouTubeStatsCard
                          stats={account.stats}
                          accountName={account.accountName}
                          onViewStats={() => handleOpenVideosDialog(account)}
                        />
                      </Grid>
                    );
                  } else if (!account.connected && (account.accountName || account.accountId)) {
                    // ✅ Token expired case
                    return (
                      <Grid item xs={12} md={6} key={account.accountId}>
                        <Card sx={{
                          borderRadius: 4,
                          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 16px 48px rgba(0,0,0,0.12)'
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar sx={{
                                bgcolor: "#FF0000",
                                width: 56,
                                height: 56,
                                boxShadow: '0 4px 16px rgba(255,0,0,0.3)'
                              }}>
                                <YouTubeIcon sx={{ color: 'white', fontSize: 28 }} />
                              </Avatar>
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>YouTube</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {account.accountName || account.accountId}
                                </Typography>
                                <Chip
                                  label="Token Expired"
                                  color="error"
                                  size="small"
                                  sx={{ mt: 1, fontWeight: 500 }}
                                />
                              </Box>
                            </Stack>
                            <Button
                              variant="contained"
                              fullWidth
                              startIcon={<AddIcon />}
                              onClick={() => handleConnectClick({ id: "youtube", name: "YouTube" })}
                              sx={{
                                mt: 3,
                                borderRadius: 2,
                                bgcolor: "#FF0000",
                                color: "white",
                                py: 1.2,
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': {
                                  bgcolor: "#CC0000"
                                }
                              }}
                            >
                              Connect Account
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  }
                }

                if (account.platform === 'facebook') {
                  if (account.connected) {
                    // ✅ Normal Facebook stats card
                    return (
                      <Grid item xs={12} md={6} key={account.accountId}>
                        <FacebookStatsCard
                          accountName={account.accountName}
                          accountId={account.accountId}
                          expiresAt={account.expiresAt}
                          onViewStats={() => handleViewFacebookInsights(account)}
                        />
                      </Grid>
                    );
                  } else if (!account.connected && (account.accountName || account.accountId)) {
                    // ✅ Token expired case for Facebook
                    return (
                      <Grid item xs={12} md={6} key={account.accountId}>
                        <Card
                          sx={{
                            borderRadius: 4,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 16px 48px rgba(0,0,0,0.12)'
                            }
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar
                                sx={{
                                  bgcolor: '#1877F2',
                                  width: 56,
                                  height: 56,
                                  boxShadow: '0 4px 16px rgba(24,119,242,0.3)'
                                }}
                              >
                                <FacebookIcon sx={{ fontSize: 28, color: 'white' }} />
                              </Avatar>
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  Facebook
                                </Typography>
                                <Tooltip title="Business Name, Account Name of the User">
                                  <Typography variant="body2" color="text.secondary">
                                    {account.accountName || account.accountId || '–'}
                                  </Typography>
                                </Tooltip>
                                <Chip label="Token Expired" color="error" size="small" sx={{ mt: 1, fontWeight: 500 }} />
                              </Box>
                            </Stack>
                            <Button
                              variant="contained"
                              fullWidth
                              startIcon={<AddIcon />}
                              onClick={() => handleConnectClick({ id: 'facebook', name: 'Facebook' })}
                              sx={{
                                mt: 3,
                                borderRadius: 2,
                                bgcolor: '#1877F2',
                                color: 'white',
                                py: 1.2,
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': { bgcolor: '#145dbf' }
                              }}
                            >
                              Connect Account
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  }
                }

                // Twitter account handling
                if (account.platform === 'twitter') {
                  if (account.connected && account.stats) {
                    // ✅ Normal Twitter stats card
                    return (
                      <Grid item xs={12} md={6} key={account.accountId}>
                        <TwitterStatsCard
                          stats={account.stats}
                          accountName={account.accountName || account.username}
                          onViewStats={() => handleOpenTwitterStatsDialog(account)}
                        />
                      </Grid>
                    );
                  } else if (!account.connected && (account.accountName || account.accountId)) {
                    // ✅ Token expired case for Twitter
                    return (
                      <Grid item xs={12} md={6} key={account.accountId}>
                        <Card sx={{
                          borderRadius: 4,
                          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 16px 48px rgba(0,0,0,0.12)'
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar sx={{
                                bgcolor: "#050505ff",
                                width: 56,
                                height: 56,
                                boxShadow: '0 4px 16px rgba(29,161,242,0.3)'
                              }}>
                                <XIcon htmlColor="white" sx={{ fontSize: 28 }} />
                              </Avatar>
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>X (Twitter)</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {account.accountName || account.accountId || account.username}
                                </Typography>
                                <Chip
                                  label="Token Expired"
                                  color="error"
                                  size="small"
                                  sx={{ mt: 1, fontWeight: 500 }}
                                />
                              </Box>
                            </Stack>
                            <Button
                              variant="contained"
                              fullWidth
                              startIcon={<AddIcon />}
                              onClick={() => handleConnectClick({ id: "twitter", name: "Twitter" })}
                              sx={{
                                mt: 3,
                                borderRadius: 2,
                                bgcolor: "#050505ff",
                                color: "white",
                                py: 1.2,
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': {
                                  bgcolor: "#1a1a1a"
                                }
                              }}
                            >
                              Connect Account
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  }
                }

                // LinkedIn account handling
                if (account.platform === 'linkedin') {
                  if (account.connected && account.stats) {
                    // ✅ Normal LinkedIn stats card
                    return (
                      <Grid item xs={12} md={6} key={account.accountId}>
                        <LinkedInStatsCard
                          stats={account.stats}
                          accountName={account.accountName}
                          onViewStats={() => handleOpenLinkedInStatsDialog(account)}
                        />
                      </Grid>
                    );
                  } else {
                    // ✅ Token expired case for LinkedIn
                    return (
                      <Grid item xs={12} md={6} key={account.accountId}>
                        <Card sx={{
                          borderRadius: 4,
                          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 16px 48px rgba(0,0,0,0.12)'
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar sx={{
                                bgcolor: "#0077B5",
                                width: 56,
                                height: 56,
                                boxShadow: '0 4px 16px rgba(0,119,181,0.3)'
                              }}>
                                <LinkedInIcon sx={{ color: 'white', fontSize: 28 }} />
                              </Avatar>
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>LinkedIn</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {account.accountName || account.accountId}
                                </Typography>
                                <Chip
                                  label="Token Expired"
                                  color="error"
                                  size="small"
                                  sx={{ mt: 1, fontWeight: 500 }}
                                />
                              </Box>
                            </Stack>
                            <Button
                              variant="contained"
                              fullWidth
                              startIcon={<AddIcon />}
                              onClick={() => handleLinkedInConnect()}
                              sx={{
                                mt: 3,
                                borderRadius: 2,
                                bgcolor: "#0077B5",
                                color: "white",
                                py: 1.2,
                                textTransform: 'none',
                                fontWeight: 600,
                                '&:hover': {
                                  bgcolor: "#005582"
                                }
                              }}
                            >
                              Reconnect Account
                            </Button>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  }
                }

                // ✅ Generic cards for other platforms
                if (account.isConnected) {
                  const platformData = platforms.find(p => p.id.toUpperCase() === account.platformType);
                  if (!platformData) return null;
                  return (
                    <Grid item xs={12} md={6} key={account.accountId}>
                      <Card sx={{
                        borderRadius: 4,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 16px 48px rgba(0,0,0,0.12)'
                        }
                      }}>
                        <CardContent sx={{ p: 3 }}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{
                              bgcolor: platformData.color,
                              width: 56,
                              height: 56,
                              boxShadow: `0 4px 16px ${platformData.color}30`
                            }}>
                              {React.createElement(platformData.icon, { sx: { color: 'white', fontSize: 28 } })}
                            </Avatar>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>{platformData.name}</Typography>
                              <Typography variant="body2" color="text.secondary">{account.accountName}</Typography>
                              <Chip
                                label="Connected"
                                color="success"
                                size="small"
                                sx={{ mt: 1, fontWeight: 500 }}
                              />
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                }

                return null;
              })}
            </Grid>
          </Box>
        )}

        {/* Available Platforms Section */}
        <Box
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onFocus={() => setIsHovered(true)}
          onBlur={() => setIsHovered(false)}
          sx={{
            position: 'relative',
            maxWidth: '100%',
            margin: '0 auto',
            px: { xs: 4, md: 6 }
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#1e293b',
              mb: 4,
              textAlign: 'center',
              fontSize: { xs: '1.5rem', md: '2rem' }
            }}
          >
            Available Platforms
          </Typography>

          {/* Left Navigation Button */}
          <IconButton
            onClick={() => scrollLeft()}
            sx={{
              position: 'absolute',
              left: { xs: -10, md: -15 },
              top: '60%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              width: { xs: 40, md: 48 },
              height: { xs: 40, md: 48 },
              opacity: canScrollLeft ? 1 : 0.3,
              transition: 'all 0.3s ease',
              borderRadius: '50%',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,1)',
                transform: 'translateY(-50%) scale(1.1)',
                boxShadow: '0 6px 30px rgba(0,0,0,0.15)'
              }
            }}
            disabled={!canScrollLeft}
          >
            <ChevronLeft sx={{ fontSize: { xs: 24, md: 28 } }} />
          </IconButton>

          {/* Right Navigation Button */}
          <IconButton
            onClick={() => scrollRight()}
            sx={{
              position: 'absolute',
              right: { xs: -10, md: -15 },
              top: '60%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: 'rgba(255,255,255,0.9)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              width: { xs: 40, md: 48 },
              height: { xs: 40, md: 48 },
              opacity: canScrollRight ? 1 : 0.3,
              transition: 'all 0.3s ease',
              borderRadius: '50%',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,1)',
                transform: 'translateY(-50%) scale(1.1)',
                boxShadow: '0 6px 30px rgba(0,0,0,0.15)'
              }
            }}
            disabled={!canScrollRight}
          >
            <ChevronRight sx={{ fontSize: { xs: 24, md: 28 } }} />
          </IconButton>

          {/* Horizontal Scrolling Container */}
          <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{
              display: 'flex',
              overflowX: 'auto',
              overflowY: 'hidden',
              height: '100%',
              p: 2.5,
              gap: { xs: 2, md: 4 },
              pb: 2,
              px: 2,
              alignItems: 'flex-start',
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              'msOverflowStyle': 'none',
              'scrollbarWidth': 'none'
            }}
          >
            {/* Create duplicated array for infinite scroll */}
            {[...platforms, ...platforms, ...platforms].map((platform, index) => {
              const IconComponent = platform.icon;
              const isConnected = connectedAccounts.some(
                acc => acc.platform === platform.id && acc.connected === true
              );


              return (
                <Card
                  key={`${platform.id}-${index}`}
                  sx={{
                    minWidth: { xs: 220, sm: 280, md: 320 },
                    maxWidth: { xs: 220, sm: 280, md: 320 },
                    height: { xs: 220, md: 440 },
                    flex: '0 0 auto',
                    flexShrink: 0,
                    border: '2px solid transparent',
                    borderRadius: 4,
                    position: 'relative',
                    overflow: 'visible',
                    background: 'rgba(255,255,255,0.9)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                    transition: 'all 0.4s ease',
                    '&:hover': {
                      transform: 'translateY(-8px) scale(1.01)',
                      boxShadow: `0 20px 60px ${platform.color}20`,
                      borderColor: platform.color,
                      '& .platform-avatar': {
                        transform: 'scale(1.1)',
                        boxShadow: `0 8px 32px ${platform.color}40`
                      }
                    }
                  }}
                >
                  {isConnected && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -12,
                        right: -12,
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        borderRadius: '50%',
                        width: { xs: 28, md: 32 },
                        height: { xs: 28, md: 32 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1,
                        border: '3px solid white',
                        boxShadow: '0 4px 16px rgba(16,185,129,0.4)'
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: { xs: 16, md: 20 }, color: 'white' }} />
                    </Box>
                  )}

                  <CardContent sx={{
                    p: { xs: 2, md: 4 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    {/* Platform Header */}
                    <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 3 }}>
                      <Avatar
                        className="platform-avatar"
                        sx={{
                          background: platform.gradient,
                          width: { xs: 36, md: 45 },
                          height: { xs: 36, md: 45 },
                          transition: 'all 0.3s ease',
                          boxShadow: `0 4px 20px ${platform.color}30`
                        }}
                      >
                        <IconComponent sx={{ color: 'white', fontSize: { xs: 24, md: 32 } }} />
                      </Avatar>
                      <Box>
                        <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 650, color: '#1e293b', mb: 0.5 }}>
                          {platform.name}
                        </Typography>
                        {isConnected && (
                          <Chip
                            label="Connected"
                            size="small"
                            sx={{
                              bgcolor: '#dcfce7',
                              color: '#166534',
                              fontWeight: 600,
                              fontSize: { xs: '0.65rem', md: '0.75rem' }
                            }}
                          />
                        )}
                      </Box>
                    </Stack>

                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        mb: 3,
                        flex: 1,
                        lineHeight: 1.6,
                        fontSize: { xs: '0.8rem', md: '0.875rem' }
                      }}
                    >
                      {platform.description}
                    </Typography>

                    {/* Features - Hide on mobile */}

                    <Box
                      sx={{
                        mb: 4,
                        display: { xs: 'none', md: 'block' } // hide on xs and sm, show on md and above
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: '#374151',
                          mb: 2
                        }}
                      >
                        Features:
                      </Typography>
                      <Stack spacing={1}>
                        {platform.features.slice(0, 4).map((feature, featureIndex) => (
                          <Stack direction="row" alignItems="center" spacing={2} key={featureIndex}>
                            <Box sx={{ color: platform.color, display: 'flex', alignItems: 'center' }}>
                              {getFeatureIcon(feature)}
                            </Box>
                            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                              {feature}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>


                    <Button
                      variant={isConnected ? "outlined" : "contained"}
                      fullWidth
                      startIcon={isConnected ? <CheckCircleIcon /> : <AddIcon />}
                      onClick={() => {
                        if (isConnected) {
                          if (window.confirm(`Remove ${platform.name} account?`)) {
                            handleDisconnectAccount(platform.id);
                          }
                        } else {
                          handleConnectClick(platform);
                        }
                      }}
                      disabled={false}
                      sx={{
                        borderRadius: 3,
                        textTransform: 'none',
                        py: { xs: 1.2, md: 1.8 },
                        fontWeight: 700,
                        fontSize: { xs: '0.85rem', md: '1rem' },
                        background: isConnected ? 'transparent' : platform.gradient,
                        borderColor: platform.color,
                        color: isConnected ? platform.color : 'white',
                        borderWidth: '2px',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderWidth: '2px',
                          transform: 'scale(1.02)',
                          bgcolor: isConnected ? `${platform.color}10` : undefined,
                          boxShadow: `0 8px 24px ${platform.color}30`
                        }
                      }}
                    >
                      {isConnected ? 'Remove Account' : 'Connect Account'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>

        {/* Connection Dialog */}
        <Dialog
          open={!!selectedPlatform}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              boxShadow: '0 32px 64px rgba(0,0,0,0.2)'
            }
          }}
        >
          {selectedPlatform && (
            <>
              <DialogTitle sx={{ pb: 2, pt: 3 }}>
                <Stack direction="row" alignItems="center" spacing={3}>
                  <Avatar
                    sx={{
                      background: selectedPlatform.gradient,
                      width: 56,
                      height: 56,
                      boxShadow: `0 4px 20px ${selectedPlatform.color}40`
                    }}
                  >
                    <selectedPlatform.icon sx={{ color: 'white', fontSize: 28 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      Connect {selectedPlatform.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {connectionStep === 1 && 'Enter your account credentials'}
                      {connectionStep === 2 && 'Verify your account'}
                      {connectionStep === 3 && 'Successfully connected!'}
                    </Typography>
                  </Box>
                </Stack>
              </DialogTitle>

              <DialogContent sx={{ pt: 3, pb: 2 }}>
                {connectionStep === 1 && (
                  <Stack spacing={4}>
                    <Alert severity="info" sx={{ borderRadius: 3, bgcolor: '#f0f9ff', border: '1px solid #0ea5e9' }}>
                      <Typography variant="body2">
                        We use secure OAuth 2.0 authentication. Your credentials are encrypted and never stored on our servers.
                      </Typography>
                    </Alert>

                    {selectedPlatform.authFields.map((field) => (
                      <Box key={field.name}>
                        {field.type === 'select' ? (
                          <FormControl fullWidth>
                            <InputLabel sx={{ fontWeight: 500 }}>{field.label}</InputLabel>
                            <Select
                              value={authData[field.name] || ''}
                              onChange={(e) => handleAuthFieldChange(field.name, e.target.value)}
                              label={field.label}
                              sx={{ borderRadius: 3 }}
                            >
                              {field.options?.map((option) => (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <TextField
                            fullWidth
                            label={field.label}
                            type={field.type}
                            required={field.required}
                            value={authData[field.name] || ''}
                            onChange={(e) => handleAuthFieldChange(field.name, e.target.value)}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 3
                              },
                              '& .MuiInputLabel-root': {
                                fontWeight: 500
                              }
                            }}
                          />
                        )}
                      </Box>
                    ))}

                    <FormControlLabel
                      control={<Switch sx={{ transform: 'scale(1.2)' }} />}
                      label={
                        <Typography sx={{ fontWeight: 500 }}>
                          Enable automatic posting
                        </Typography>
                      }
                      sx={{ mt: 2 }}
                    />
                  </Stack>
                )}

                {connectionStep === 2 && (
                  <Stack spacing={4} alignItems="center">
                    <Box sx={{ textAlign: 'center' }}>
                      <SecurityIcon sx={{ fontSize: 64, color: selectedPlatform.color, mb: 2 }} />
                      <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
                        Verification Required
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                        We've sent a verification code to your {selectedPlatform.name} account.
                        Please check your notifications and enter the code below.
                      </Typography>
                    </Box>

                    <TextField
                      fullWidth
                      label="Verification Code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      placeholder="Enter 6-digit code"
                      inputProps={{
                        maxLength: 6,
                        style: {
                          textAlign: 'center',
                          fontSize: '1.5rem',
                          fontWeight: 700,
                          letterSpacing: '0.2em'
                        }
                      }}
                      sx={{
                        maxWidth: 280,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3
                        }
                      }}
                    />

                    <Button
                      variant="text"
                      size="small"
                      startIcon={<RefreshIcon />}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        color: selectedPlatform.color
                      }}
                    >
                      Resend Code
                    </Button>
                  </Stack>
                )}

                {connectionStep === 3 && (
                  <Stack spacing={4} alignItems="center">
                    <CheckCircleIcon sx={{ fontSize: 80, color: '#10b981' }} />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ mb: 2, color: '#10b981', fontWeight: 800 }}>
                        Successfully Connected!
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        Your {selectedPlatform.name} account has been connected successfully.
                        You can now schedule and manage posts from this platform.
                      </Typography>
                    </Box>

                    <Paper sx={{ p: 3, bgcolor: '#f0fdf4', width: '100%', borderRadius: 3, border: '1px solid #bbf7d0' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#166534' }}>
                        What's Next?
                      </Typography>
                      <List dense>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <ScheduleIcon sx={{ fontSize: 24, color: '#10b981' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography sx={{ fontWeight: 600 }}>Schedule your first post</Typography>}
                            secondary="Go to Schedule Posts to create content"
                          />
                        </ListItem>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon>
                            <TrendingUpIcon sx={{ fontSize: 24, color: '#10b981' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={<Typography sx={{ fontWeight: 600 }}>View analytics</Typography>}
                            secondary="Track your performance and engagement"
                          />
                        </ListItem>
                      </List>
                    </Paper>
                  </Stack>
                )}
              </DialogContent>

              <DialogActions sx={{ p: 4, pt: 2 }}>
                {connectionStep === 1 && (
                  <>
                    <Button
                      onClick={handleCloseDialog}
                      sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      disabled={isConnecting || (!authData.email && !authData.username)}
                      startIcon={isConnecting ? <CircularProgress size={20} /> : <LaunchIcon />}
                      onClick={() => setConnectionStep(2)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        px: 4,
                        py: 1.2,
                        borderRadius: 3,
                        background: selectedPlatform.gradient,
                        '&:hover': {
                          transform: 'scale(1.02)'
                        }
                      }}
                    >
                      {isConnecting ? 'Connecting...' : 'Connect Account'}
                    </Button>
                  </>
                )}

                {connectionStep === 2 && (
                  <>
                    <Button
                      onClick={handleCloseDialog}
                      sx={{ textTransform: 'none', fontWeight: 600, px: 3 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleVerification}
                      disabled={isConnecting || !verificationCode || verificationCode.length !== 6}
                      startIcon={isConnecting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        px: 4,
                        py: 1.2,
                        borderRadius: 3,
                        background: selectedPlatform.gradient,
                        '&:hover': {
                          transform: 'scale(1.02)'
                        }
                      }}
                    >
                      {isConnecting ? 'Verifying...' : 'Verify Account'}
                    </Button>
                  </>
                )}

                {connectionStep === 3 && (
                  <Button
                    variant="contained"
                    onClick={handleCloseDialog}
                    fullWidth
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      py: 1.8,
                      borderRadius: 3,
                      bgcolor: '#10b981',
                      fontSize: '1.1rem',
                      '&:hover': {
                        bgcolor: '#059669',
                        transform: 'scale(1.02)'
                      }
                    }}
                  >
                    Done
                  </Button>
                )}
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
      <Dialog open={videosDialogOpen} onClose={handleCloseVideosDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          {selectedAccountForVideos && (
            <>
              <Avatar sx={{ bgcolor: getPlatformData(selectedAccountForVideos.platform)?.color, mr: 2 }}>
                {React.createElement(getPlatformData(selectedAccountForVideos.platform)?.icon)}
              </Avatar>
              <Box>
                <Typography variant="h6">Recent Content Stats</Typography>
                <Typography variant="body2" color="text.secondary">{selectedAccountForVideos.accountName}</Typography>
              </Box>
            </>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <List sx={{ maxHeight: 450, overflowY: 'auto', p: 0 }}>
            {recentVideos.length > 0 ? recentVideos.map((video, index) => (
              <React.Fragment key={video.id}> {/* ✅ Correctly using video.id for the key */}

                {/* ✅ MODIFIED ListItem to use the video.videoUrl directly */}
                <ListItem
                  button
                  component="a"
                  href={video.videoUrl} // ✅ Using the direct URL from your data
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <ListItemAvatar>
                    <Avatar variant="rounded" src={video.thumbnailUrl} sx={{ width: 128, height: 72, mr: 2 }} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={video.title}
                    primaryTypographyProps={{ fontWeight: 500, mb: 1.5 }}
                    secondary={
                      <Stack direction="row" spacing={3} alignItems="center">
                        <Tooltip title="Views">
                          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.secondary' }}>
                            <VisibilityIcon sx={{ fontSize: 18 }} />
                            <Typography variant="body2">{Number(video.views).toLocaleString()}</Typography>
                          </Stack>
                        </Tooltip>
                        <Tooltip title="Likes">
                          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.secondary' }}>
                            <ThumbUpIcon sx={{ fontSize: 18 }} />
                            <Typography variant="body2">{Number(video.likes).toLocaleString()}</Typography>
                          </Stack>
                        </Tooltip>
                        <Tooltip title="Comments">
                          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.secondary' }}>
                            <CommentIcon sx={{ fontSize: 18 }} />
                            <Typography variant="body2">{Number(video.comments).toLocaleString()}</Typography>
                          </Stack>
                        </Tooltip>
                      </Stack>
                    }
                  />
                  <LaunchIcon sx={{ color: 'action.active', ml: 2, alignSelf: 'center' }} />
                </ListItem>

                {index < recentVideos.length - 1 && <Divider variant="inset" component="li" />}
              </React.Fragment>
            )) : (
              <Typography sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                No recent video data available for this account.
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseVideosDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
